import { CHANNEL_TYPES } from "../constants/channelCatalog";

const GROUP_PREFIX_RE = /^grupo\s*:/i;

export function stripWhatsappGroupPrefix(value) {
  const v = String(value ?? "").trim();
  if (!v) return "";
  if (GROUP_PREFIX_RE.test(v)) return v.replace(GROUP_PREFIX_RE, "").trim();
  return v;
}

function parseMessageWhatsappJson(message) {
  const raw = message?.json_whatsapp;
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function extractCreatedWhatsappGroupJid(payload) {
  if (!payload || typeof payload !== "object") return "";
  const nested =
    payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)
      ? payload.data
      : payload;
  const deeper =
    nested.data && typeof nested.data === "object" && !Array.isArray(nested.data)
      ? nested.data
      : nested;
  const candidates = [
    deeper.id,
    deeper.gid,
    deeper.jid,
    deeper.groupId,
    deeper.group_id,
    deeper.grupo_id,
    deeper.group?.id,
    deeper.grupo?.id,
    nested.id,
    payload.id,
  ];
  for (const candidate of candidates) {
    const jid = extractWhatsappGroupJid(candidate);
    if (jid) return jid;
    const raw = String(candidate || "").trim();
    if (/^\d{8,}$/.test(raw)) return `${raw}@g.us`;
  }
  return "";
}

export function extractWhatsappGroupJid(value) {
  const raw = String(value ?? "").trim();
  if (!raw || raw === "true") return "";
  const intern = raw.match(/^apifacil-group:[^:]+:(.+)$/i);
  if (intern) {
    const inner = String(intern[1] || "").trim();
    if (/@g\.us$/i.test(inner)) return inner;
    if (/^\d{8,}$/.test(inner)) return `${inner}@g.us`;
  }
  const match = raw.match(/(\d{8,}@g\.us)\b/i);
  if (match) return match[1];
  const stripped = stripWhatsappGroupPrefix(raw);
  if (/@g\.us$/i.test(stripped)) return stripped;
  if (/^\d{8,}$/.test(stripped) && GROUP_PREFIX_RE.test(raw)) return `${stripped}@g.us`;
  if (/^120\d{11,}$/.test(stripped.replace(/\D/g, ""))) {
    return `${stripped.replace(/\D/g, "")}@g.us`;
  }
  return "";
}

export function resolveWhatsappGroupId(conversation, messages = []) {
  const client = conversation?.client && typeof conversation.client === "object" ? conversation.client : null;
  const candidates = [
    conversation?.id_interno,
    conversation?.idInterno,
    conversation?.internalId,
    client?.id_interno,
    conversation?.whatsappPhone,
    conversation?.celular,
    conversation?.mobile,
    conversation?.phone,
    conversation?.phoneNumber,
    conversation?.whatsapp,
    client?.whatsappPhone,
    client?.mobile,
  ];
  const jids = [];
  for (const c of candidates) {
    const jid = extractWhatsappGroupJid(c);
    if (jid) jids.push(jid);
  }
  for (const m of Array.isArray(messages) ? messages : []) {
    const json = parseMessageWhatsappJson(m);
    for (const c of [
      m?.group?.id,
      m?.xpro_group?.id,
      m?.whatsappContext?.group?.id,
      json?.xpro_group?.id,
      json?.grupo_id,
      json?.id_grupo,
      json?.grupo,
      json?.remote_jid,
      json?.destino,
      json?.numero_telefone_destino,
    ]) {
      const jid = extractWhatsappGroupJid(c);
      if (jid) jids.push(jid);
    }
  }
  if (jids.length) {
    return [...new Set(jids)].sort((a, b) => b.length - a.length)[0];
  }
  for (const c of candidates) {
    const raw = String(c ?? "").trim();
    if (!GROUP_PREFIX_RE.test(raw)) continue;
    const id = stripWhatsappGroupPrefix(raw);
    if (id && id !== "true") return id;
  }
  return null;
}

export function isWhatsappGroupConversation(conversation, messages = []) {
  if (!conversation) return false;
  const client = conversation.client && typeof conversation.client === "object" ? conversation.client : null;
  const intern = String(
    conversation.id_interno || conversation.idInterno || conversation.internalId || client?.id_interno || ""
  ).trim();
  if (/^apifacil-group:/i.test(intern)) return true;
  if (resolveWhatsappGroupId(conversation, messages)) return true;
  return (Array.isArray(messages) ? messages : []).some(
    (m) => Boolean(m?.isGroupMessage || m?.group?.id || m?.whatsappContext?.group?.id)
  );
}

export function isWhatsappQrGroupComposer({ channelTipo, conversation, messages } = {}) {
  if (Number(channelTipo) !== CHANNEL_TYPES.WHATSAPP_QR) return false;
  return isWhatsappGroupConversation(conversation, messages);
}

export function isWhatsappMentionOption(option) {
  const type = option?.type;
  return type === "whatsapp" || type === "whatsapp_all";
}

export function groupMentionPickerOptions(options) {
  const list = Array.isArray(options) ? options : [];
  const whatsapp = [];
  const internal = [];
  for (const option of list) {
    if (isWhatsappMentionOption(option)) whatsapp.push(option);
    else internal.push(option);
  }
  return { whatsapp, internal };
}

export function mentionTokenFromValue(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (raw.includes("@")) return raw.split("@")[0];
  return raw.replace(/\D/g, "") || raw;
}

export function isWhatsappLidValue(value) {
  return /@lid$/i.test(String(value ?? "").trim());
}

export function looksLikeWhatsappGroupUserId(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return false;
  if (/@g\.us$/i.test(raw) || GROUP_PREFIX_RE.test(raw)) return true;
  const digits = raw.replace(/@.+$/, "").replace(/\D/g, "");
  return /^120\d{11,}$/.test(digits);
}

export function looksLikeWhatsappLidUser(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return false;
  if (isWhatsappLidValue(raw)) return true;
  if (/@(s\.whatsapp\.net|g\.us)$/i.test(raw)) return false;
  if (looksLikeWhatsappGroupUserId(raw)) return false;
  const digits = raw.replace(/@.+$/, "").replace(/\D/g, "");
  if (digits.length >= 14 && !/^(55|1)\d+$/.test(digits)) return true;
  if (digits.length === 13 && !/^(55|1)/.test(digits)) return true;
  return false;
}

export function whatsappPhoneMatchKey(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits || looksLikeWhatsappLidUser(digits)) return "";
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    return digits.slice(-8);
  }
  return digits.length >= 10 ? digits : "";
}

/** Telefone (DDI+número) ou JID `@lid`. Grupo `@g.us` não é menção. */
export function sendableWhatsappMentionToken(value) {
  const raw = String(value ?? "").trim();
  if (!raw || looksLikeWhatsappGroupUserId(raw)) return "";
  if (isWhatsappLidValue(raw) || looksLikeWhatsappLidUser(raw)) {
    const user = raw.split("@")[0].replace(/\D/g, "");
    return user.length >= 8 ? `${user}@lid` : "";
  }
  const user = raw.includes("@") ? raw.split("@")[0] : raw;
  const digits = String(user).replace(/\D/g, "");
  return digits.length >= 8 ? digits : "";
}

function participantNameKey(name) {
  const n = String(name || "")
    .toLowerCase()
    .replace(/^~/, "")
    .replace(/\[.*?\]/g, " ")
    .replace(/[^a-z0-9à-öø-ÿ\s]/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!n) return "";
  const parts = n.split(" ").filter(Boolean);
  return parts.slice(0, 2).join(" ");
}

export function participantMentionPhone(p) {
  const fromPhone = sendableWhatsappMentionToken(p?.phone);
  if (fromPhone && !isWhatsappLidValue(fromPhone)) return fromPhone;
  if (isWhatsappLidValue(p?.jid) || isWhatsappLidValue(p?.lid)) return "";
  const fromJid = sendableWhatsappMentionToken(p?.jid);
  return fromJid && !isWhatsappLidValue(fromJid) ? fromJid : "";
}

/** Token de menção nativa: LID do grupo privado, senão telefone. */
export function participantMentionToken(p) {
  const lid = sendableWhatsappMentionToken(p?.lid || (isWhatsappLidValue(p?.jid) ? p.jid : ""));
  if (lid) return lid;
  return participantMentionPhone(p);
}

export function mentionDisplayName(name, fallback = "") {
  const clean = String(name || "")
    .replace(/^@+/, "")
    .replace(/\s+/g, " ")
    .trim();
  if (clean) return clean;
  return sendableWhatsappMentionToken(fallback) || "";
}

export function isUsableWhatsappParticipantName(name, identity = {}) {
  const clean = String(name || "")
    .replace(/^@+/, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!clean) return false;
  if (/@(lid|s\.whatsapp\.net|g\.us)$/i.test(clean)) return false;
  if (/^grupo\s+\d+/i.test(clean)) return false;
  if (/^(cliente|atendente|sistema|agente de ia|chatbot|participante)$/i.test(clean)) return false;
  const digits = clean.replace(/\D/g, "");
  const phoneDigits = String(identity.phone || "").replace(/\D/g, "");
  const lidDigits = String(identity.lid || identity.jid || "")
    .replace(/@.+$/, "")
    .replace(/\D/g, "");
  if (digits && (digits === phoneDigits || digits === lidDigits)) return false;
  if (/^\d{8,}$/.test(clean)) return false;
  if (looksLikeWhatsappLidUser(clean)) return false;
  return true;
}

export function mentionStillInText(plainText, mentionValue, mentionName) {
  const text = String(plainText || "").replace(/\u00a0/g, " ");
  const token = mentionTokenFromValue(mentionValue);
  if (token && (text.includes(`@${token}`) || text.includes(`@${mentionValue}`))) return true;
  const name = mentionDisplayName(mentionName);
  return Boolean(name && text.includes(`@${name}`));
}

function participantLidToken(p) {
  return (
    sendableWhatsappMentionToken(p?.lid) ||
    (isWhatsappLidValue(p?.jid) || looksLikeWhatsappLidUser(p?.jid)
      ? sendableWhatsappMentionToken(p.jid)
      : "")
  );
}

function pickBetterParticipantName(prevName, nextName, identity) {
  if (isUsableWhatsappParticipantName(prevName, identity)) return String(prevName).trim();
  if (isUsableWhatsappParticipantName(nextName, identity)) return String(nextName).trim();
  return "";
}

function participantIdentityKeys(p) {
  const keys = [];
  const lid = participantLidToken(p);
  const phone = participantMentionPhone(p);
  if (lid) keys.push(`lid:${lid}`);
  if (phone) {
    keys.push(`phone:${phone}`);
    const br = whatsappPhoneMatchKey(phone);
    if (br) keys.push(`br:${br}`);
  }
  return { keys, lid, phone };
}

export function participantsFromGroupMessages(messages = [], options = {}) {
  const groupName = String(options.groupName || "").trim();
  const extracted = [];
  for (const m of Array.isArray(messages) ? messages : []) {
    const ctx = m?.whatsappContext && typeof m.whatsappContext === "object" ? m.whatsappContext : null;
    const json = parseMessageWhatsappJson(m);
    const identityHint = {
      phone: String(m?.participant?.phone || json?.numero_telefone_origem || "").replace(/\D/g, ""),
      lid: String(json?.numero_origem_lid || json?.participant || m?.participant?.jid || ""),
      jid: String(m?.participant?.jid || json?.participant || ""),
    };
    const nameCandidates = [
      json?.pushName,
      json?.push_name,
      json?.notify,
      json?.nome_contato,
      json?.nome_remetente,
      json?.contact_name,
      json?.verifiedName,
      ctx?.contactProfile?.pushName,
      ctx?.contactProfile?.name,
      m?.participant?.name,
      m?.senderType === "client" || m?.isGroupMessage || m?.group?.id ? m?.senderName : null,
    ];
    let name = "";
    for (const candidate of nameCandidates) {
      const text = String(candidate || "").trim();
      if (!text || text === groupName) continue;
      if (isUsableWhatsappParticipantName(text, identityHint)) {
        name = text;
        break;
      }
    }
    const lidContext = json?.eh_lid === true || ctx?.addressing?.isLid === true;
    const rawCandidates = [
      json?.participant_alt,
      json?.remote_jid_alt,
      ctx?.contactProfile?.wa_id,
      json?.numero_telefone_origem,
      json?.numero_origem,
      !lidContext ? json?.origem : null,
      m?.participant?.phone,
      json?.participant,
      ctx?.addressing?.participant,
    ];
    let phone = "";
    let jid = "";
    let lid = "";
    for (const candidate of [
      json?.numero_origem_lid,
      json?.participant,
      ctx?.addressing?.participant,
      m?.participant?.jid,
    ]) {
      const value = String(candidate || "").trim();
      if (!isWhatsappLidValue(value) && !looksLikeWhatsappLidUser(value)) continue;
      const token = sendableWhatsappMentionToken(value);
      if (token) {
        lid = token;
        break;
      }
    }
    for (const candidate of rawCandidates) {
      const value = String(candidate || "").trim();
      if (!value || isWhatsappLidValue(value) || looksLikeWhatsappLidUser(value) || /@g\.us$/i.test(value) || GROUP_PREFIX_RE.test(value)) {
        continue;
      }
      const token = sendableWhatsappMentionToken(value);
      if (token && !isWhatsappLidValue(token) && !looksLikeWhatsappLidUser(token)) {
        phone = token;
        jid = value.includes("@") ? value : `${token}@s.whatsapp.net`;
        break;
      }
    }
    if (!phone && !lid) continue;
    extracted.push({
      jid: lid || jid || (phone ? `${phone}@s.whatsapp.net` : ""),
      lid: lid || undefined,
      phone: phone || "",
      name,
      isAdmin: false,
    });
  }
  return mergeWhatsappGroupParticipants(extracted);
}

export function mergeWhatsappGroupParticipants(...lists) {
  const rows = [];
  const index = new Map();

  const remember = (rowIdx) => {
    const { keys } = participantIdentityKeys(rows[rowIdx]);
    for (const key of keys) index.set(key, rowIdx);
  };

  for (const list of lists) {
    for (const p of Array.isArray(list) ? list : []) {
      if (
        looksLikeWhatsappGroupUserId(p?.phone) ||
        looksLikeWhatsappGroupUserId(p?.lid) ||
        looksLikeWhatsappGroupUserId(p?.jid)
      ) {
        continue;
      }
      const { keys, lid, phone } = participantIdentityKeys(p);
      if (!keys.length) continue;
      let found = -1;
      for (const key of keys) {
        if (index.has(key)) {
          const idx = index.get(key);
          if (rows[idx]) {
            found = idx;
            break;
          }
        }
      }
      const identity = {
        phone: phone || (found >= 0 ? rows[found].phone : ""),
        lid: lid || (found >= 0 ? rows[found].lid : ""),
        jid: p?.jid || (found >= 0 ? rows[found].jid : ""),
      };
      if (found >= 0) {
        const prev = rows[found];
        const nextPhone = prev.phone || phone;
        const nextLid = prev.lid || lid;
        rows[found] = {
          ...prev,
          ...p,
          phone: nextPhone || "",
          lid: nextLid || undefined,
          name: pickBetterParticipantName(prev.name, p.name, { ...identity, phone: nextPhone, lid: nextLid }),
          jid: nextLid || (!isWhatsappLidValue(prev.jid) ? prev.jid : "") || p.jid || (nextPhone ? `${nextPhone}@s.whatsapp.net` : ""),
          isAdmin: Boolean(prev.isAdmin || p.isAdmin),
          isSelf: Boolean(prev.isSelf || p.isSelf || prev.isMe || p.isMe),
        };
        const absorb = new Set();
        for (const key of participantIdentityKeys(rows[found]).keys) {
          if (index.has(key) && index.get(key) !== found) absorb.add(index.get(key));
          index.set(key, found);
        }
        for (const other of absorb) {
          const extra = rows[other];
          if (!extra) continue;
          const phone2 = rows[found].phone || extra.phone;
          const lid2 = rows[found].lid || extra.lid;
          rows[found] = {
            ...rows[found],
            ...extra,
            phone: phone2 || "",
            lid: lid2 || undefined,
            name: pickBetterParticipantName(rows[found].name, extra.name, {
              phone: phone2,
              lid: lid2,
              jid: rows[found].jid || extra.jid,
            }),
            jid: lid2 || rows[found].jid || extra.jid,
            isAdmin: Boolean(rows[found].isAdmin || extra.isAdmin),
            isSelf: Boolean(rows[found].isSelf || extra.isSelf),
          };
          rows[other] = null;
        }
        remember(found);
        continue;
      }
      rows.push({
        ...p,
        phone: phone || "",
        lid: lid || undefined,
        name: pickBetterParticipantName("", p.name, identity),
        jid: lid || p.jid || (phone ? `${phone}@s.whatsapp.net` : ""),
      });
      remember(rows.length - 1);
    }
  }

  const compact = rows.filter(Boolean);
  rows.length = 0;
  rows.push(...compact);
  index.clear();
  compact.forEach((_, idx) => remember(idx));

  const unresolved = [];
  for (const list of lists) {
    for (const p of Array.isArray(list) ? list : []) {
      const { keys } = participantIdentityKeys(p);
      if (keys.length) continue;
      unresolved.push(p);
    }
  }
  for (const p of unresolved) {
    const nameKey = participantNameKey(p?.name);
    if (!nameKey || !isUsableWhatsappParticipantName(p?.name, p)) continue;
    for (const row of rows) {
      if (participantNameKey(row.name) !== nameKey) continue;
      if (!isUsableWhatsappParticipantName(row.name, row)) row.name = p.name || row.name;
      const lid = participantLidToken(p) || row.lid || "";
      if (lid) {
        row.lid = lid;
        if (!row.jid || !isWhatsappLidValue(row.jid)) row.jid = lid;
      }
      break;
    }
  }

  const used = new Set();
  const coalesced = [];
  for (let i = 0; i < rows.length; i += 1) {
    if (used.has(i)) continue;
    let row = rows[i];
    const nameKey = participantNameKey(row.name);
    if (nameKey && isUsableWhatsappParticipantName(row.name, row)) {
      for (let j = i + 1; j < rows.length; j += 1) {
        if (used.has(j)) continue;
        const other = rows[j];
        if (participantNameKey(other.name) !== nameKey) continue;
        const oneLidOnly = Boolean((row.lid && !row.phone) || (other.lid && !other.phone));
        const onePhone = Boolean(row.phone || other.phone);
        if (!oneLidOnly || !onePhone) continue;
        const nextPhone = row.phone || other.phone;
        const nextLid = row.lid || other.lid;
        const identity = { phone: nextPhone, lid: nextLid, jid: row.jid || other.jid };
        const phoneRowName = row.phone ? row.name : other.name;
        row = {
          ...row,
          ...other,
          phone: nextPhone,
          lid: nextLid,
          name:
            pickBetterParticipantName(phoneRowName, "", identity) ||
            pickBetterParticipantName(row.name, other.name, identity),
          jid: nextLid || row.jid || other.jid,
        };
        used.add(j);
      }
    }
    used.add(i);
    coalesced.push(row);
  }
  return coalesced;
}

export function isWhatsappGroupSelfParticipant(p, selfList = []) {
  if (!p) return false;
  if (p.isSelf === true || p.isMe === true) return true;
  const lid = participantLidToken(p);
  const phone = participantMentionPhone(p);
  const phoneKey = whatsappPhoneMatchKey(phone);
  for (const self of Array.isArray(selfList) ? selfList : [selfList]) {
    if (!self) continue;
    if (self.isMe === true || self.isSelf === true) return true;
    const selfLid = participantLidToken(self) || sendableWhatsappMentionToken(self.lid || self.jid);
    const selfPhone = participantMentionPhone(self) || (
      looksLikeWhatsappLidUser(self.phone) ? "" : sendableWhatsappMentionToken(self.phone)
    );
    if (lid && selfLid && lid === selfLid) return true;
    if (phone && selfPhone && !isWhatsappLidValue(selfPhone) && !looksLikeWhatsappLidUser(selfPhone)) {
      if (phone === selfPhone) return true;
      const selfKey = whatsappPhoneMatchKey(selfPhone);
      if (phoneKey && selfKey && phoneKey === selfKey) return true;
    }
  }
  return false;
}

export function excludeWhatsappGroupSelf(list = [], selfList = []) {
  return (Array.isArray(list) ? list : []).filter((p) => !isWhatsappGroupSelfParticipant(p, selfList));
}

export function selfIdentitiesFromGroupMessages(messages = []) {
  const out = [];
  const seen = new Set();
  for (const m of Array.isArray(messages) ? messages : []) {
    const json = parseMessageWhatsappJson(m);
    const fromMe = json?.fromMe === true || json?.from_me === true;
    if (!fromMe) continue;
    const members = participantsFromGroupMessages([m]);
    for (const p of members) {
      const key = participantLidToken(p) || participantMentionPhone(p);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(p);
    }
  }
  return out;
}

function mentionLabelMatchesComposer(plainText, label) {
  const name = mentionDisplayName(label);
  if (!name) return false;
  const raw = String(plainText || "")
    .replace(/\u00a0/g, " ")
    .trimStart();
  if (!raw.startsWith("@")) return false;
  const after = raw.slice(1);
  return after === name || after.startsWith(`${name} `) || after.startsWith(`${name}\n`);
}

/** Texto do compositor ainda é uma menção a membro do grupo WhatsApp (não @ de colega). */
export function composerHasWhatsappGroupMention({
  groupId,
  plainText,
  mentions = [],
  mentionNames = {},
  mentionAll = false,
} = {}) {
  if (!groupId) return false;
  if (mentionAll === true) return true;
  const text = String(plainText || "");
  const labels = mentionNames && typeof mentionNames === "object" ? mentionNames : {};
  for (const raw of Array.isArray(mentions) ? mentions : []) {
    if (
      mentionStillInText(text, raw, labels[raw] || labels[String(raw)]) ||
      mentionLabelMatchesComposer(text, labels[raw] || labels[String(raw)])
    ) {
      return true;
    }
  }
  return false;
}

/** Nome de quem mandou a mensagem no grupo — nunca LID/telefone/título do grupo. */
export function resolveWhatsappGroupSenderDisplayName(message, options = {}) {
  const json = parseMessageWhatsappJson(message);
  const ctx = message?.whatsappContext && typeof message.whatsappContext === "object" ? message.whatsappContext : null;
  const groupName = String(
    options.groupName ||
      message?.group?.name ||
      json?.xpro_group?.name ||
      json?.nome_grupo ||
      ""
  ).trim();
  const identity = {
    phone: String(message?.participant?.phone || json?.numero_telefone_origem || json?.participant_alt || "").replace(/\D/g, ""),
    lid: String(json?.numero_origem_lid || json?.participant || message?.participant?.jid || ""),
    jid: String(message?.participant?.jid || json?.participant || ""),
  };
  const candidates = [
    json?.pushName,
    json?.push_name,
    json?.notify,
    json?.nome_contato,
    json?.nome_remetente,
    json?.contact_name,
    json?.verifiedName,
    ctx?.contactProfile?.pushName,
    ctx?.contactProfile?.name,
    message?.participant?.name,
    message?.senderName,
  ];
  for (const candidate of candidates) {
    const text = String(candidate || "").trim();
    if (!text || text === groupName) continue;
    if (isUsableWhatsappParticipantName(text, identity)) return text;
  }
  const phone = participantMentionPhone({
    phone: message?.participant?.phone || json?.participant_alt || json?.remote_jid_alt,
  });
  return phone || "Participante";
}

export function mentionsForOutboundPayload({
  enabled,
  mentions,
  mentionAll,
  mentionNames,
  plainText,
} = {}) {
  if (!enabled) return {};
  const labels = mentionNames && typeof mentionNames === "object" ? mentionNames : {};
  const kept = [];
  const seen = new Set();
  for (const raw of Array.isArray(mentions) ? mentions : []) {
    const token = sendableWhatsappMentionToken(raw);
    if (!token || seen.has(token)) continue;
    if (
      !mentionStillInText(plainText, token, labels[token] || labels[raw]) &&
      !mentionStillInText(plainText, raw, labels[raw])
    ) {
      continue;
    }
    seen.add(token);
    kept.push(token);
  }
  const names = {};
  for (const token of kept) {
    const label = mentionDisplayName(labels[token]);
    if (label) names[token] = label;
  }
  const all = mentionAll === true;
  if (!all && !kept.length) return {};
  return {
    ...(kept.length ? { mentions: kept } : {}),
    ...(Object.keys(names).length ? { mentionNames: names } : {}),
    ...(all ? { mentionAll: true } : {}),
  };
}
